/*
  Stacked mode (§6.3): below md each row is a card and each cell is a labelled field.

  A description list, because that is what the rows have become — the column name is the
  term and the cell is its description. No zebra striping: §6.3 exempts the cards, and a
  card already separates itself from its neighbour with a border and a ground. The hover
  treatment and the focus ring do apply, so a card holding a link still highlights.

  The population report is opened on phones from the storefronts, which is why this exists
  at all rather than a sideways-scrolling table.
*/

import { renderCellContent } from "./columns";
import { STACKED_CARD, TABLE_CELL_PADDING } from "./tableStyles";
import { DataTableColumn } from "./types";

export interface DataTableStackedListProps<TRow> {
  columns: readonly DataTableColumn<TRow>[];
  rows: readonly TRow[];
  getRowKey: (row: TRow, index: number) => string | number;
  /* The list's accessible name — the same one the table's caption carries. */
  label: string;
}

function DataTableStackedList<TRow>(props: DataTableStackedListProps<TRow>) {
  const { columns, rows, getRowKey, label } = props;

  return (
    <ul aria-label={label} className="flex flex-col gap-3">
      {rows.map((row, index) => (
        <li key={getRowKey(row, index)} className={STACKED_CARD}>
          <dl className="divide-y divide-pcg-borderSoft">
            {columns.map((column) => (
              <div
                key={column.id}
                className={`${TABLE_CELL_PADDING} flex items-start justify-between gap-3`}
              >
                {column.labelHidden ? null : (
                  <dt className="shrink-0 text-xs font-semibold uppercase tracking-wide text-pcg-textMuted">
                    {column.label}
                  </dt>
                )}
                <dd
                  className={`min-w-0 text-sm text-pcg-text ${
                    column.labelHidden ? "w-full text-left" : "text-right"
                  }`}
                >
                  {renderCellContent(column, row)}
                </dd>
              </div>
            ))}
          </dl>
        </li>
      ))}
    </ul>
  );
}

export default DataTableStackedList;
